// @flow

import React, { Component, type Node } from "react";
import { bindAll, noop } from "lodash";
import type { UnaryFn, BinaryFn } from "@tvg/types/Functional";
import type { Device } from "@tvg/types/Device";
import {
  getTrackDataSourceTitle,
  TALENT_PICKS,
  TRACKMASTER_PICKS,
  TIMEFORM_PICKS,
  HANDICAPPING_STORE,
  TRACK_DATA_SOURCE_USGSA
} from "@tvg/utils/trackMasterPicksUtils";

import TabGroup from "../../_molecule/TabGroup";
import TitleHeader from "../../_molecule/TitleHeader";
import {
  TabsContainer,
  TextHighlighted,
  ComponentContainer
} from "./styled-components";

export type TabName =
  | typeof TALENT_PICKS
  | typeof TRACKMASTER_PICKS
  | typeof TIMEFORM_PICKS
  | typeof HANDICAPPING_STORE;

type State = {
  tab: TabName
};

type Props = {
  /**
   * Number of available talent picks
   */
  tpCounter: number,
  /**
   * Indicates if the talent picks are available to see or not
   */
  isTalentPicksAvailable: boolean,
  /**
   * Calback that returns the component to render as the tab content
   */
  componentRender: UnaryFn<TabName, Node>,
  /**
   * Calback to run code on the component on the tab change event
   */
  onTabChange: BinaryFn<TabName, string, void>,
  /**
   * Flag set via toggle
   */
  picksTabsEnabled: boolean,
  /**
   * If timeform tab is enabled on program page
   */
  timeformTabEnabled: boolean,
  /**
   * If handicapping store tab is enabled on program page
   */
  handicapStoreTabEnabled: boolean,
  /**
   * The device this is displaying on
   */
  device: Device,
  /**
   * Get track data source
   */
  trackDataSource: string
};

export default class ProgramPagePicks extends Component<Props, State> {
  static defaultProps = {
    tpCounter: 0,
    isTalentPicksAvailable: true,
    timeformTabEnabled: false,
    handicapStoreTabEnabled: false,
    componentRender: noop,
    onTabChange: noop,
    trackDataSource: "",
    device: "mobile"
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      tab: TRACKMASTER_PICKS
    };

    bindAll(this, "buildTabs", "changeTab");
  }

  hasPickInText = (trackDataSource: string) => trackDataSource === "SRW";

  changeTab(tabName: TabName, tabTitle: string) {
    this.setState({
      tab: tabName
    });

    this.props.onTabChange(tabName, tabTitle);
  }

  buildTabs() {
    const tabs = [];
    const {
      isTalentPicksAvailable,
      tpCounter,
      trackDataSource,
      timeformTabEnabled,
      handicapStoreTabEnabled
    } = this.props;

    const tabsTitle = {
      TALENT_PICKS: "TVG Picks",
      TIMEFORM_PICKS: "numberFire Prediction",
      TRACKMASTER_PICKS: "Trackmaster",
      HANDICAPPING_STORE: "Handicapping Store"
    };

    tabs.push({
      title: tabsTitle.TALENT_PICKS,
      subTitle: !isTalentPicksAvailable ? (
        <TextHighlighted>Unavailable in Pick</TextHighlighted>
      ) : (
        ""
      ),
      qaLabel: "talentTab",
      isActive: this.state.tab === TALENT_PICKS,
      counter: isTalentPicksAvailable ? tpCounter : 0,
      isDisabled: !isTalentPicksAvailable || tpCounter === 0,
      onClick: () =>
        tpCounter > 0 && this.changeTab(TALENT_PICKS, tabsTitle.TALENT_PICKS),
      isTitleUppercase: false
    });

    if (timeformTabEnabled && trackDataSource === TRACK_DATA_SOURCE_USGSA) {
      tabs.push({
        title: tabsTitle.TIMEFORM_PICKS,
        isActive: this.state.tab === TIMEFORM_PICKS,
        qaLabel: "timeformTab",
        onClick: () => this.changeTab(TIMEFORM_PICKS, tabsTitle.TIMEFORM_PICKS),
        isTitleUppercase: false
      });
    }

    const title = getTrackDataSourceTitle(
      trackDataSource,
      this.hasPickInText(trackDataSource)
    );
    tabs.push({
      title,
      isActive: this.state.tab === TRACKMASTER_PICKS,
      qaLabel: "trackMasterTab",
      onClick: () => this.changeTab(TRACKMASTER_PICKS, title),
      isTitleUppercase: false
    });

    if (handicapStoreTabEnabled) {
      tabs.push({
        title: tabsTitle.HANDICAPPING_STORE,
        isActive: this.state.tab === HANDICAPPING_STORE,
        qaLabel: "handicappingStoreTab",
        onClick: () =>
          this.changeTab(HANDICAPPING_STORE, tabsTitle.HANDICAPPING_STORE),
        isTitleUppercase: false
      });
    }

    return tabs;
  }

  render() {
    const { device, picksTabsEnabled, trackDataSource, componentRender } =
      this.props;

    return (
      <TabsContainer device={device} data-qa-label="picksSection">
        {picksTabsEnabled && (
          <TabGroup
            tabs={this.buildTabs()}
            stretch={this.buildTabs().length < 4}
          />
        )}
        <ComponentContainer>
          {!picksTabsEnabled && (
            <TitleHeader
              data-qa-label="trackMasterPicks-title"
              title={getTrackDataSourceTitle(trackDataSource, true)}
            />
          )}
          {componentRender(this.state.tab)}
        </ComponentContainer>
      </TabsContainer>
    );
  }
}
